import { BadgeCheck, ExternalLink, Lock, Star } from "lucide-react";

import type { AppEntitlement } from "@/types/app";
import { cn } from "@/utils/cn";

type Props = {
  entitlement: AppEntitlement;
  isFavorite?: boolean;
  onToggleFavorite?: (id: string) => void;
  onLaunch: (entitlement: AppEntitlement) => void;
};

export function AppCard({
  entitlement,
  isFavorite,
  onToggleFavorite,
  onLaunch,
}: Props) {
  const { app, visible } = entitlement;
  const disabled = !visible || app.status === "coming_soon" || !app.url;
  const isUrlIcon = /^https?:\/\//.test(app.icon);

  return (
    <div
      className={cn(
        "card group relative flex h-full flex-col gap-3 p-4 transition-shadow hover:shadow-tileHover",
        disabled && "opacity-75",
      )}
    >
      <div className="flex items-start justify-between">
        <div
          className={cn(
            "flex h-11 w-11 items-center justify-center rounded text-xl font-semibold",
            app.accent ?? "bg-slate-100 text-slate-700",
          )}
          aria-hidden="true"
        >
          {isUrlIcon ? (
            <img src={app.icon} alt="" className="h-8 w-8 rounded" />
          ) : (
            <span>{app.icon}</span>
          )}
        </div>
        {onToggleFavorite ? (
          <button
            type="button"
            onClick={() => onToggleFavorite(app.id)}
            aria-label={isFavorite ? "Remove favorite" : "Add favorite"}
            aria-pressed={isFavorite}
            className="rounded p-1 text-slate-400 opacity-0 transition-opacity hover:text-amber-500 group-hover:opacity-100 focus-visible:opacity-100"
          >
            <Star
              className={cn(
                "h-4 w-4",
                isFavorite && "fill-amber-400 text-amber-500",
              )}
            />
          </button>
        ) : null}
      </div>

      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">
            {app.name}
          </h3>
          {app.status === "coming_soon" ? (
            <span className="chip bg-slate-100 text-slate-600">Coming soon</span>
          ) : null}
          {!visible ? (
            <span className="chip bg-red-50 text-red-700">
              <Lock className="h-3 w-3" />
              Restricted
            </span>
          ) : app.assignedToMe ? (
            <span
              className="chip bg-emerald-50 text-emerald-700"
              title="Assigned to you in Entra (Enterprise Application → Users and groups)"
            >
              <BadgeCheck className="h-3 w-3" />
              Assigned
            </span>
          ) : null}
        </div>
        <p className="mt-1 line-clamp-2 text-xs text-slate-500">
          {app.description}
        </p>
      </div>

      <div className="flex items-center justify-between pt-1">
        <span className="chip">{app.category}</span>
        <button
          type="button"
          onClick={() => onLaunch(entitlement)}
          disabled={disabled}
          className={cn(
            "btn-secondary text-xs",
            disabled && "cursor-not-allowed opacity-50",
          )}
        >
          Launch
          {app.launchType !== "same_tab" ? (
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          ) : null}
        </button>
      </div>
    </div>
  );
}
